import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import _ from 'lodash'

import CircularProgress from '@material-ui/core/CircularProgress'
import { Checkbox } from 'ui/components'

import { fetchPlanServicesList as fetchPlanCpfServicesList } from 'redux/planCpfServices/actions'
import { fetchPlanServicesList as fetchPlanCnpjServicesList } from 'redux/planCnpjServices/actions'

import analysisStatusConstants from 'utils/constants/analysisStatus'
import { GROUP_ID } from 'utils/constants/groupService'
import { PANEL_ENVS } from 'utils/constants/environments'

import {
  additionalCostText,
  expensiveServiceText,
  isManualInputService,
  renderPlanGroup,
  renderSelectProcessPole,
} from './renderFunctions'
import { typeResultMoveToForcedValue } from './planForcedData'

import styles, { Container } from './styles'

// const reprocessInfoText = 'Quando esta opção for checada, caso haja algum erro durante o processo de análise, o documento será reprocessado automaticamente.'

// Creates an object with the number of selected services of each group
const initializeGroups = (services) => {
  if (_.isEmpty(services)) return {}

  const data = {}

  _.each(_.keys(GROUP_ID), (groupId) => {
    data[groupId] = _.countBy(services, (service) => service.group_id === groupId && service.checked).true || 0
  })

  return data
}

export default function ({
  docType,
  processPole,
  planServices,
  readOnly = false,
  onChangeProcessPole = null,
  onChangePlanServices = null,
}) {
  const classes = styles()

  const dispatch = useDispatch()

  let servicesList = null
  switch (docType) {
    case PANEL_ENVS.CPF:
      servicesList = useSelector((state) => state.planCpfServices.services)
      break
    case PANEL_ENVS.CNPJ:
      servicesList = useSelector((state) => state.planCnpjServices.services)
      break
    default:
  }

  const [planServicesCurrent, setPlanServicesCurrent] = useState(planServices)

  const [loading, setLoading] = useState(true)
  const [groupsSelected, setGroupsSelected] = useState({})
  const [showServiceIdLabel, setShowServiceIdLabel] = useState(false)

  const setPlanServices = (val) => {
    const servicesChecked = _.filter(val, { checked: true })
    setPlanServicesCurrent(val)
    onChangePlanServices(servicesChecked)
  }

  const initializeScoreService = (newServicesList) => {
    const service = _.find(newServicesList, (s) => (s.service_id.includes('credit_score')))
    if (_.isNil(service)) return

    _.each(service.type_results, typeResult => {
      if (_.isNil(typeResult.data)) return

      _.each(typeResult.data, (v, _k) => {
        if (!_.isNil(v.value)) return

        v.value = 300
      })
    })
  }

  const initializeProcessClassifier = (newServicesList) => {
    const service = _.find(newServicesList, { service_id: 'processes_classifier' })
    if (_.isNil(service)) return

    // add default use_process_contingency
    if (_.isEmpty(_.get(service, 'data.use_process_contingency'))) {
      _.set(service, 'data.use_process_contingency', true)
    }

    _.each(service.type_results, typeResult => {
      if (_.isNil(typeResult.data)) return

      const defaultMoveTo = typeResult.move_to
      typeResult.move_to = typeResultMoveToForcedValue(
        service.service_id,
        typeResult.type_result_id,
        defaultMoveTo,
      )

      _.each(typeResult.data, (v, _k) => {
        if (!_.isNil(v.values)) return

        v.values = []
      })
    })
  }

  const initializeMembershipBoardRecursive = (newServicesList) => {
    const service = _.find(newServicesList, { service_id: 'membership_board_recursive' })
    if (_.isNil(service)) return

    _.each(service.type_results, typeResult => {
      if (_.isNil(typeResult.data)) return

      _.each(typeResult.data, (v, _k) => {
        if (!_.isNil(v.value)) return

        v.value = 0
      })
    })
  }

  useEffect(() => {
    switch (docType) {
      case PANEL_ENVS.CPF:
        dispatch(fetchPlanCpfServicesList())
        break
      case PANEL_ENVS.CNPJ:
        dispatch(fetchPlanCnpjServicesList())
        break
      default:
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (_.isEmpty(servicesList)) return
    // if (_.isEmpty(planServicesCurrent)) return

    const newServicesList = _.cloneDeep(servicesList)

    // initialize fields
    _.each(newServicesList, (service) => {
      service.checked = false
      _.each(service.type_results, typeResult => {
        typeResult.move_to = analysisStatusConstants.SERVICE_ANALYSIS_STATUS_MOVE_TO.reproved
      })
    })

    // add default values [] to processes_classifier
    initializeProcessClassifier(newServicesList)

    _.each(planServicesCurrent, (serviceCurr) => {
      const service = _.find(newServicesList, { service_id: serviceCurr.service_id })
      if (_.isEmpty(service)) return

      service.checked = true

      if (serviceCurr.data) service.data = serviceCurr.data

      _.each(serviceCurr.type_results, typeResultCurr => {
        const typeResult = _.find(
          service.type_results,
          { type_result_id: typeResultCurr.type_result_id },
        )

        typeResult.move_to = typeResultMoveToForcedValue(service.service_id, typeResult.type_result_id, typeResultCurr.move_to)

        if (!_.isNil(typeResult.data)) {
          typeResult.data = _.merge(typeResultCurr.data, typeResult.data)
        }
      })
    })

    // add default value (300) to score services
    initializeScoreService(newServicesList)

    // add default value (0) to max_depth
    initializeMembershipBoardRecursive(newServicesList)

    setPlanServicesCurrent(newServicesList)
    setGroupsSelected(initializeGroups(newServicesList))
    setLoading(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleChangeSelectProcessPole = (event) => {
    onChangeProcessPole(event.target.value)
  }

  const handleChangeData = (serviceId, data) => {
    if (_.isEmpty(data)) return

    const newServices = _.cloneDeep(planServicesCurrent)
    const service = _.find(newServices, { service_id: serviceId })

    if (_.isEmpty(service.data)) service.data = {}
    service.data = { ...service.data, ...data }

    setPlanServices([...newServices])
  }

  const onUpdateTypeResults = (serviceId, typeResults) => {
    const newServices = _.cloneDeep(planServicesCurrent)
    const serviceIndex = _.findIndex(newServices, { service_id: serviceId })

    _.each(typeResults, (newTypeResult) => {
      const serviceTypeResult = _
      .chain(newServices)
      .get(`${serviceIndex}.type_results`)
      .find(typeRes => typeRes.type_result_id === newTypeResult.type_result_id)
      .value()

      serviceTypeResult.move_to = newTypeResult.move_to

      _.each(newTypeResult.data, (item, itemName) => {
        _.set(serviceTypeResult, `data.${itemName}.values`, [...item.values])
        serviceTypeResult.data = { ...serviceTypeResult.data }
      })
    })

    setPlanServices([...newServices])
  }

  const handleClickRadio = (serviceId, typeResultId, moveTo) => {
    const newServices = _.cloneDeep(planServicesCurrent)
    const serviceIndex = _.findIndex(newServices, { service_id: serviceId })

    const typeResult = _
      .chain(newServices)
      .get(`${serviceIndex}.type_results`)
      .find(typeRes => typeRes.type_result_id === typeResultId)
      .value()

    typeResult.move_to = moveTo

    setPlanServices(newServices)
  }

  const handleChangeChips = (chips, serviceId, typeResultId, fieldData) => {
    const newServices = _.cloneDeep(planServicesCurrent)
    const serviceIndex = _.findIndex(newServices, { service_id: serviceId })

    const typeResult = _
      .chain(newServices)
      .get(`${serviceIndex}.type_results`)
      .find(typeRes => typeRes.type_result_id === typeResultId)
      .value()

    _.set(typeResult, `data.${fieldData}.values`, chips)

    setPlanServices(newServices)
  }

  const handleChangeSlider = (sliderValue, serviceId, typeResultId, fieldData) => {
    const newServices = _.cloneDeep(planServicesCurrent)
    const serviceIndex = _.findIndex(newServices, { service_id: serviceId })

    const typeResult = _
      .chain(newServices)
      .get(`${serviceIndex}.type_results`)
      .find(typeRes => typeRes.type_result_id === typeResultId)
      .value()

    _.set(typeResult, `data.${fieldData}.value`, sliderValue)

    setPlanServices(newServices)
  }

  const handleCheckService = (serviceId, checked) => {
    const newServices = _.cloneDeep(planServicesCurrent)
    const service = _.find(newServices, { service_id: serviceId })

    if (checked === true && service.additional_cost)
      if (!window.confirm(additionalCostText(service.additional_cost, true)))
        return

    if (checked === true && service.expensive_service)
      if (!window.confirm(expensiveServiceText(true)))
        return

    service.checked = checked

    setPlanServices([...newServices])

    const groups = _.cloneDeep(groupsSelected)

    groups[service.group_id] += checked ? 1 : -1
    setGroupsSelected(groups)
  }

  const handleCheckServiceGroup = (groupId, checked) => {
    let servicesSelected = 0
    const newServices = _.map(planServicesCurrent, (service) => {
      if (service.group_id !== groupId) return service
      if (
        checked &&
        (
          service.additional_cost ||
          service.expensive_service ||
          service.gov_br_auth_required ||
          isManualInputService(service)
        )
      ) return service

      service.checked = checked

      if (checked) servicesSelected += 1

      return service
    })

    setPlanServices([...newServices])

    const groups = _.cloneDeep(groupsSelected)
    groups[groupId] = servicesSelected
    setGroupsSelected(groups)
  }

  if (loading) return <CircularProgress />

  const renderCheckboxShowServiceIdLabel = () => {
    return (
      <div className={classes.checkboxShowServiceIdLabelContainer}>
        <Checkbox
          label="Exibir service_id dos itens"
          onClick={setShowServiceIdLabel}
          value={showServiceIdLabel}
        />
      </div>
    )
  }

  const groups = _.keys(GROUP_ID)

  return (
    <Container>
      {renderSelectProcessPole(processPole, handleChangeSelectProcessPole, readOnly)}

      <div className={classes.labelCheckboxes}>
        Selecione os itens que irão setar o status de uma pesquisa automaticamente, caso haja alguma inconformidade na análise:
      </div>

      {renderCheckboxShowServiceIdLabel()}

      {_.map(
        groups,
        (gId) => renderPlanGroup(
          gId,
          planServicesCurrent,
          servicesList,
          docType,
          groupsSelected,
          handleCheckServiceGroup,
          handleChangeChips,
          handleChangeSlider,
          handleClickRadio,
          handleCheckService,
          onUpdateTypeResults,
          handleChangeData,
          classes,
          readOnly,
          showServiceIdLabel,
        ),
      )}
    </Container>
  )
}
